<div class="keep-shell">

  <!-- HEADER / SEARCH --------------------------------------------------- -->
  <header class="keep-header">
    <div class="brand">
      <span class="logo-dot"></span>
      <span class="logo-text">Resume Homie</span>
    </div>

    <form [formGroup]="searchForm" class="search">
      <mat-icon class="search-icon">search</mat-icon>
      <input formControlName="query" placeholder="Search resumes by text or label…" />
    </form>

    <div class="header-actions">
      <button mat-button (click)="toggleArchivedView()">
        <mat-icon>{{ showArchived ? 'unarchive' : 'archive' }}</mat-icon>
        <span>{{ showArchived ? 'Show active' : 'Show archived' }}</span>
      </button>
    </div>
  </header>

  <!-- LABEL FILTERS ----------------------------------------------------- -->
  <section class="labels" *ngIf="labels.length">
    <button
      mat-stroked-button
      [color]="!selectedLabel ? 'primary' : ''"
      (click)="selectLabel(null)">
      All
    </button>

    <button
      mat-stroked-button
      *ngFor="let label of labels"
      [class.active]="label === selectedLabel"
      (click)="selectLabel(label)">
      {{ label }}
    </button>
  </section>

  <!-- GENERATE NEW RESUME (NOTES-LIKE CARD) ---------------------------- -->
  <section class="new-note">
    <form [formGroup]="generateForm" (ngSubmit)="generateFromSelected()">
      <div class="new-note-card" [style.backgroundColor]="generateForm.value.color">

        <div class="top-row">
          <input
            type="text"
            class="title"
            placeholder="New resume title (e.g., Senior AI Engineer - Google)"
            formControlName="title" />

          <div class="controls-inline">
            <mat-icon
              class="pin-toggle"
              [class.pinned]="generateForm.value.pinned"
              (click)="generateForm.patchValue({ pinned: !generateForm.value.pinned })"
              matTooltip="Pin this generated resume">
              push_pin
            </mat-icon>

            <div class="color-dots">
              <span
                *ngFor="let c of colorPalette"
                class="color-dot"
                [class.selected]="generateForm.value.color === c"
                [style.backgroundColor]="c"
                (click)="generateForm.patchValue({ color: c })">
              </span>
            </div>

            <button
              mat-raised-button
              color="primary"
              type="submit"
              [disabled]="!canGenerate">
              {{ isLoading ? 'Generating…' : 'Generate' }}
            </button>
          </div>
        </div>

        <!-- MODE CHOOSER -->
        <div class="mode-tabs">
          <button
            type="button"
            class="tab"
            [class.active]="baseMode === 'select'"
            (click)="generateForm.patchValue({ baseMode: 'select', saveBaseToBoard: true })">
            <mat-icon>folder</mat-icon>
            Select from board
          </button>

          <button
            type="button"
            class="tab"
            [class.active]="baseMode === 'paste'"
            (click)="generateForm.patchValue({ baseMode: 'paste', saveBaseToBoard: true })">
            <mat-icon>content_paste</mat-icon>
            Paste resume
          </button>

          <button
            type="button"
            class="tab"
            [class.active]="baseMode === 'upload'"
            (click)="generateForm.patchValue({ baseMode: 'upload', saveBaseToBoard: true })">
            <mat-icon>upload</mat-icon>
            Upload resume
          </button>
        </div>

        <!-- 50/50 INPUTS -->
        <div class="split-grid">

          <!-- LEFT: CURRENT RESUME -->
          <section class="pane">
            <div class="pane-title">
              <mat-icon>description</mat-icon>
              <span>Current resume</span>
            </div>

            <!-- SELECT MODE -->
            <div class="base-picker" *ngIf="baseMode === 'select'">
              <mat-icon>folder</mat-icon>

              <select formControlName="baseResumeId" [disabled]="!isLoggedIn || isLoading">
                <option value="" disabled>Select a resume from your board…</option>
                <option *ngFor="let r of resumes" [value]="r.id">
                  {{ r.title || 'Untitled' }} — {{ r.source === 'uploaded' ? 'Uploaded' : 'Generated' }}
                </option>
              </select>

              <span class="base-badge" *ngIf="selectedBase">
                Using: <strong>{{ selectedBase.title || 'Untitled' }}</strong>
                <span class="pill" [class.uploaded]="selectedBase.source === 'uploaded'">
                  {{ selectedBase.source === 'uploaded' ? 'Uploaded' : 'Generated' }}
                </span>
              </span>
            </div>

            <!-- PASTE MODE -->
            <textarea
              *ngIf="baseMode === 'paste'"
              class="content-box"
              placeholder="Paste your current resume text…"
              formControlName="baseResumeText"
              rows="10">
            </textarea>

            <!-- UPLOAD MODE -->
            <div *ngIf="baseMode === 'upload'" class="upload-box">
              <div class="upload-row">
                <input
                  type="file"
                  class="file"
                  (change)="onUploadResumeFile($event)"
                  accept=".txt,.md,.rtf,text/plain" />

                <span class="file-name" *ngIf="uploadFileName">
                  <mat-icon>attach_file</mat-icon>
                  {{ uploadFileName }}
                </span>
              </div>

              <textarea
                class="content-box"
                placeholder="Uploaded text will appear here (you can edit it)…"
                formControlName="baseResumeText"
                rows="10">
              </textarea>

              <div class="upload-hint">
                Tip: For PDF/DOCX, paste extracted text instead (this upload reads text-based files).
              </div>
            </div>

            <!-- SAVE NEW CARD (paste/upload only) -->
            <div class="save-row" *ngIf="baseMode !== 'select'">
              <mat-checkbox formControlName="saveBaseToBoard">
                Save base resume to board as a new card
              </mat-checkbox>

              <button
                type="button"
                mat-stroked-button
                (click)="saveBaseAsNewCard()"
                [disabled]="!isLoggedIn || isLoading || !(generateForm.value.baseResumeText || '').trim()">
                <mat-icon>note_add</mat-icon>
                Add to board
              </button>
            </div>
          </section>

          <!-- RIGHT: JOB DESCRIPTION -->
          <section class="pane">
            <div class="pane-title">
              <mat-icon>work</mat-icon>
              <span>Job description</span>
            </div>

            <textarea
              class="content-box"
              placeholder="Paste the job description…"
              formControlName="jobDescription"
              rows="10">
            </textarea>
          </section>
        </div>

        <!-- LABELS -->
        <div class="meta-row">
          <div class="labels-input">
            <mat-icon>label</mat-icon>
            <input
              type="text"
              formControlName="labels"
              placeholder="Labels (comma separated) e.g. Google, Remote, AI" />
          </div>
        </div>

        <div class="rb-error" *ngIf="error">
          <mat-icon>warning</mat-icon>
          <span>{{ error }}</span>
        </div>

        <div class="hint" *ngIf="!isLoggedIn">
          Sign in to view your board, save resumes, and generate tailored versions.
        </div>

      </div>
    </form>
  </section>

  <!-- PINNED RESUMES --------------------------------------------------- -->
  <section class="notes-section" *ngIf="filteredPinned.length">
    <h3>Pinned</h3>

    <div
      cdkDropList
      [cdkDropListData]="pinnedResumes"
      (cdkDropListDropped)="dropPinned($event)"
      class="notes-grid">

      <article
        class="note-card resume-card"
        *ngFor="let r of filteredPinned"
        cdkDrag
        [style.backgroundColor]="r.color || '#ffffff'">

        <header class="note-header">
          <h4 class="note-title">{{ r.title || 'Untitled resume' }}</h4>

          <div class="right-actions">
            <span class="pill" [class.uploaded]="r.source === 'uploaded'">
              {{ r.source === 'uploaded' ? 'Uploaded' : 'Generated' }}
            </span>

            <button mat-icon-button (click)="togglePinned(r, false)" matTooltip="Unpin">
              <mat-icon>push_pin</mat-icon>
            </button>
          </div>
        </header>

        <div class="note-content" [class.truncated]="isLong(r)">
          {{ r.content || '(No content)' }}
        </div>

        <footer class="note-footer">
          <div class="labels-chips">
            <span class="chip" *ngFor="let label of (r.labels || [])">{{ label }}</span>
          </div>

          <div class="note-actions">
            <button mat-icon-button (click)="export(r, 'pdf')" matTooltip="Export PDF">
              <mat-icon>picture_as_pdf</mat-icon>
            </button>

            <button mat-icon-button (click)="export(r, 'docx')" matTooltip="Export DOCX">
              <mat-icon>description</mat-icon>
            </button>

            <button mat-icon-button (click)="archive(r)" matTooltip="Archive / restore">
              <mat-icon>{{ r.archived ? 'unarchive' : 'archive' }}</mat-icon>
            </button>

            <button mat-icon-button [matMenuTriggerFor]="colorMenuPinned" matTooltip="Color">
              <mat-icon>palette</mat-icon>
            </button>

            <button mat-icon-button (click)="delete(r)" matTooltip="Delete">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </footer>

        <mat-menu #colorMenuPinned="matMenu">
          <button
            mat-menu-item
            *ngFor="let c of colorPalette"
            (click)="changeColor(r, c)">
            <span class="menu-color-dot" [style.backgroundColor]="c"></span>
          </button>
        </mat-menu>
      </article>
    </div>
  </section>

  <!-- OTHER RESUMES ---------------------------------------------------- -->
  <section class="notes-section" *ngIf="filteredOthers.length">
    <h3>Others</h3>

    <div
      cdkDropList
      [cdkDropListData]="otherResumes"
      (cdkDropListDropped)="dropOthers($event)"
      class="notes-grid">

      <article
        class="note-card resume-card"
        *ngFor="let r of filteredOthers"
        cdkDrag
        [style.backgroundColor]="r.color || '#ffffff'">

        <header class="note-header">
          <h4 class="note-title">{{ r.title || 'Untitled resume' }}</h4>

          <div class="right-actions">
            <span class="pill" [class.uploaded]="r.source === 'uploaded'">
              {{ r.source === 'uploaded' ? 'Uploaded' : 'Generated' }}
            </span>

            <button mat-icon-button (click)="togglePinned(r, true)" matTooltip="Pin">
              <mat-icon>push_pin</mat-icon>
            </button>
          </div>
        </header>

        <div class="note-content" [class.truncated]="isLong(r)">
          {{ r.content || '(No content)' }}
        </div>

        <footer class="note-footer">
          <div class="labels-chips">
            <span class="chip" *ngFor="let label of (r.labels || [])">{{ label }}</span>
          </div>

          <div class="note-actions">
            <button mat-icon-button (click)="export(r, 'pdf')" matTooltip="Export PDF">
              <mat-icon>picture_as_pdf</mat-icon>
            </button>

            <button mat-icon-button (click)="export(r, 'docx')" matTooltip="Export DOCX">
              <mat-icon>description</mat-icon>
            </button>

            <button mat-icon-button (click)="archive(r)" matTooltip="Archive / restore">
              <mat-icon>{{ r.archived ? 'unarchive' : 'archive' }}</mat-icon>
            </button>

            <button mat-icon-button [matMenuTriggerFor]="colorMenuOther" matTooltip="Color">
              <mat-icon>palette</mat-icon>
            </button>

            <button mat-icon-button (click)="delete(r)" matTooltip="Delete">
              <mat-icon>delete</mat-icon>
            </button>
          </div>
        </footer>

        <mat-menu #colorMenuOther="matMenu">
          <button
            mat-menu-item
            *ngFor="let c of colorPalette"
            (click)="changeColor(r, c)">
            <span class="menu-color-dot" [style.backgroundColor]="c"></span>
          </button>
        </mat-menu>
      </article>
    </div>
  </section>

  <!-- EMPTY STATE ------------------------------------------------------ -->
  <section class="empty-state" *ngIf="!filteredPinned.length && !filteredOthers.length">
    <mat-icon>folder_open</mat-icon>
    <p>
      Your resumes will appear here. Upload one, paste one, or generate one — then tailor it to a job description.
    </p>
  </section>

</div>
